<script setup lang="ts">
import { ref } from "vue";
import ColorPickerBody from "./ColorPickerBody.vue";

const props = withDefaults(
	defineProps<{
		inline?: boolean;
		onColorClick?: ((paletteUuid: string, hexColor: string) => void) | null;
		showLockedOntoForRandomize?: boolean;
		location?: "top" | "bottom" | "start" | "end" | "center";
		variant?: "default" | "filled";
		height?: number;
	}>(),
	{
		inline: false,
		onColorClick: null,
		showLockedOntoForRandomize: false,
		location: "bottom",
		variant: "default",
		height: 48,
	}
);

const selectedColor = defineModel<string>();
const emit = defineEmits(["color-updated"]);

const isMenuOpen = ref(false);

const onApply = (color: string, paletteUuid: string) => {
	selectedColor.value = color;
	emit("color-updated", color);
	props.onColorClick?.(paletteUuid, color);
	isMenuOpen.value = false;
};

const onStageColor = (color: string, paletteUuid: string) => {
	if (!props.inline) {
		// Popup mode: preset click immediately commits
		selectedColor.value = color;
		emit("color-updated", color);
		props.onColorClick?.(paletteUuid, color);
		isMenuOpen.value = false;
	}
	// Inline mode: staging is handled inside ColorPickerBody (RGB sliders update)
	// — no model update until Apply is clicked
};

function bindActivator(props: Record<string, any>) {
	return {
		...props,
		onClick: (event: MouseEvent) => {
			event.preventDefault();
			props.onClick?.(event);
		},
	};
}
</script>

<template>
	<!-- Inline mode: render picker directly in page -->
	<div v-if="inline" class="inline-picker-wrapper">
		<color-picker-body
			:initial-color="selectedColor"
			@apply="onApply"
			@stage-color="onStageColor"
		/>
	</div>

	<!-- Popup mode (default): preserve existing behaviour exactly -->
	<v-menu
		v-else
		v-model="isMenuOpen"
		:close-on-content-click="false"
		transition="scale-transition"
		:location="location"
	>
		<template #activator="{ props: activatorProps }">
			<div v-bind="bindActivator(activatorProps)">
				<slot>
					<div class="d-flex align-center">
						<v-spacer />
						<div v-if="showLockedOntoForRandomize" class="pr-3">
							<v-icon size="20">fa: fa-light fa-crosshairs</v-icon>
							<v-tooltip
								activator="parent"
								location="top"
								content-class="text-small"
							>
								Selecting the first color sets the randomizer’s starting point
								and fills the remaining colors from the chosen palette.
							</v-tooltip>
						</div>
						<div
							v-if="variant === 'default'"
							class="color-preview"
							:style="{ backgroundColor: selectedColor }"
						/>
						<v-sheet
							v-else-if="variant === 'filled'"
							color="background"
							:height="props.height"
							width="48"
							class="d-flex align-center justify-center rounded-t"
							style="border-bottom: 1px solid #bdc1c9"
						>
							<div
								class="color-preview"
								:style="{ backgroundColor: selectedColor }"
							/>
						</v-sheet>
					</div>
				</slot>
			</div>
		</template>

		<v-card color="surface">
			<v-toolbar flat>
				<v-toolbar-title>
					<i class="fa-duotone fa-palette fa-lg mr-3" />
					Color Picker
				</v-toolbar-title>
				<v-spacer />
				<v-btn icon @click="isMenuOpen = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider style="border-color: unset !important" />
			<div class="pa-2">
				<color-picker-body
					:initial-color="selectedColor"
					@apply="onApply"
					@stage-color="onStageColor"
				/>
			</div>
		</v-card>
	</v-menu>
</template>

<style scoped lang="scss">
.color-preview {
	cursor: pointer;
	appearance: none;
	height: 16px;
	width: 16px;
	background-color: #ccc;
	border-radius: 50%;
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5),
		inset 0 -1px 2px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1);
	border: 1px solid rgba(0, 0, 0, 0.1);
}

.inline-picker-wrapper {
	width: 100%;
}
</style>
